import {
    Component,
    Input,
    Output,
    EventEmitter,
    Renderer2,
    Inject,
    OnChanges,
    SimpleChanges,
    ViewChild,
    AfterViewInit
} from '@angular/core';
import { ISelectedQuotation, ILoaders } from 'libraries/hotel/models/quotation';
import { yii } from './config/yii';
import { IProperty } from 'libraries/hotel/models/quote';
import { CONFIG, LOADERS } from './config/init';
import { ICurrencies } from "../../../service/resources/hotel/ISearchResponse";
import { HotelCurrencyService } from "../../../service/hotel-currency/hotel-currency.service";
import { IConfigSharedHotel } from "../../models/config-shared-hotel";
import { ICurrency } from "../../../service/resources/model/search/ICurrency";
import { IMapConfiguration } from 'libraries/interfaces/IMapConfiguration';
import { wenji } from 'libraries/interfaces/IWenji';
import { IMarkerPoint, MarkerColor, MarkerSize, IPoint } from 'libraries/map/IPoint';
import { IOverlay } from 'libraries/hotel/containers/hotels-list/services/IOverlay';
import { MapComponent } from 'libraries/map/map.component';
import { IMapBounds, IMinimumBounds } from 'libraries/map/IMapBounds';
import { MapComputationService } from 'libraries/map/computation/map-computation.service';
import { ILabels } from "../../../interfaces/ILabels";
import { IGetHotelQuote } from "../../containers/hotels-list/hotels-list.component";
import { DOCUMENT } from "@angular/common";
import _cloneDeep from "lodash-es/cloneDeep";

interface IMarkerPointData extends IMarkerPoint {
    overlay: IOverlay;
}

@Component({
    selector: 'iqbh-map',
    template: `
        <div *ngIf="isMapFullscreen" class="mask"></div>

        <card [class.mapFullscreen]="isMapFullscreen">
            <div class="card-header-content">
                <h1>{{ overlays.length }} {{ yii.hotelsFoundTitle }}</h1>

                <div>
                    <btn type="actionUndo"
                         *ngIf="!isBatchContext"
                         [value]="isMapFullscreen ? yii.mapReduce : yii.mapFullscreen"
                         (onClick)="toggleMapFullscreen()"></btn>
                    <btn *ngIf="config.hasList"
                         class="rm-margin"
                         type="actionUndo"
                         [value]="yii.showList"
                         (onClick)="showList()"></btn>
                </div>
            </div>

            <div class="card-body-content">
                <ng-content></ng-content>

                <loader *ngIf="loaders.searchHotels || loaders.getQuotation" [message]="getLoaderMessage(loaders)"></loader>
                <map #map
                     *ngIf="overlays && !loaders.searchHotels && !loaders.getQuotation"
                     [configuration]="mapConfiguration"
                     [points]="markers"
                     [defaultBounds]="defaultBounds"
                     [activateSearch]="config.searchMap.active"
                     [searchText]="defaultSearch"
                     (onMarkerClick)="onMarkerClick($event)"
                     (onBoundsChanged)="onBoundsChanged($event)"></map>

                <div class="selected-hotel">
                    <p *ngIf="!selectedQ && markers && markers.length">{{ yii.selectAnHotel }}</p>

                    <hotel-resume *ngIf="selectedQ"
                                  [currency]="currency"
                                  [hotel]="getHotelProperty(selectedQ.hotelInformations)"
                                  [mealPlan]="getHotelProperty(selectedQ.hotelInformations).mealplan"></hotel-resume>

                    <div *ngIf="selectedQ">
                        <btn *ngIf="config.hasOffer"
                             type="actionMore"
                             [value]="yii.offersAndDetails"
                             (click)="showDetailsAndOffers()"></btn>
                        <btn class="rm-margin"
                             type="actionPrincipal"
                             [value]="yii.chooseQuotation"
                             (click)="getQuotation()"></btn>
                    </div>
                </div>
            </div>
        </card>
    `,
    styleUrls: ['./iqbh-map.component.scss']
})
export class IqbhMapComponent implements OnChanges, AfterViewInit {
    public yii: ILabels = yii;

    @Input()
    public isBatchContext: boolean = false;

    @Input()
    public overlays: IOverlay[] = [];

    @Input()
    public selectedQuotation: ISelectedQuotation;

    @Input()
    public currencies: ICurrencies;

    @Input()
    public configSharedHotel: IConfigSharedHotel;

    @Input()
    public loaders: {
        getQuotation?: boolean;
        searchHotels?: boolean;
    } = LOADERS;

    @Input()
    public config: {
        hasOffer: boolean;
        hasList: boolean;
        searchMap: {
            active: boolean;
            placeholder: string;
        };
    } = CONFIG;

    @Input()
    public defaultSearch: string = '';

    @Input()
    public geoposition: IIqbhCoordinates;

    @Output()
    public readonly onShowDetailsAndOffers: EventEmitter<IGetHotelQuote> = new EventEmitter();

    @Output()
    public readonly onShowList: EventEmitter<null> = new EventEmitter();

    @Output()
    public readonly onChoose: EventEmitter<IGetHotelQuote> = new EventEmitter();

    @Output()
    public readonly onGetGeoposition: EventEmitter<IIqbhCoordinates> = new EventEmitter();

    @ViewChild('map')
    public map: MapComponent;

    public selectedQ: ISelectQuote;
    public isMapFullscreen: boolean = false;
    public currency: ICurrency;
    public mapConfiguration: IMapConfiguration = wenji.ui.map;
    public markers: IMarkerPointData[];
    public defaultBounds: IMinimumBounds;

    constructor(
        private readonly renderer: Renderer2,
        private readonly hotelCurrencyService: HotelCurrencyService,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly mapComputationService: MapComputationService,
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.overlays) {
            this.generateMarkers();
        }

        if (changes.selectedQuotation) {
            if (this.selectedQuotation && this.selectedQuotation.selected) {
                const property: IProperty = this.selectedQuotation.hotels[this.selectedQuotation.selected].property;
                this.selectedQ = {
                    request: {
                        properties: this.selectedQuotation.selected,
                        trips: this.selectedQuotation.hotels[this.selectedQuotation.selected].property.trip,
                    },
                    hotelInformations: property
                };
            }

            this.generateMarkers();
        }

        if (changes.geoposition) {
            this.defaultBounds = this.getBounds(this.geoposition);
        }
    }

    public ngAfterViewInit(): void {
        if(this.geoposition){
            this.defaultBounds = this.getBounds(this.geoposition);
        }
    }

    public onBoundsChanged(bounds: IMapBounds): void {
        // Round the max radius
        const max: number = Math.max(
                bounds.distanceInMeters.ew,
                bounds.distanceInMeters.ns
            ) / 2;
        this.onGetGeoposition.emit({
            latitude: bounds.center.latitude,
            longitude: bounds.center.longitude,
            radius: {
                unit: 'm',
                max,
            }
        });
    }

    public getLoaderMessage(loader: ILoaders): string {
        return loader.searchHotels ? yii.hotelsLoading : yii.generateQuotation;
    }

    public markerClick({property, trip, hotel}: IMarkerClick): void {
        this.selectedQ = {request: {properties: property, trips: trip}, hotelInformations: hotel};
        const currency: string = this.hotelCurrencyService.getCurrencyString(
            hotel.total.cost,
            this.configSharedHotel
        );
        this.currency = this.currencies[currency];
    }

    public toggleMapFullscreen(): void {
        this.isMapFullscreen = !this.isMapFullscreen;

        // If map on fullscreen, hide the overflow of body
        if (this.isMapFullscreen) {
            this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
        } else {
            this.renderer.setStyle(this.document.body, 'overflow', 'auto');
        }

        setTimeout(() => {
            this.map.invalidateSize();
        }, 10);

    }

    public showDetailsAndOffers(): void {
        this.onShowDetailsAndOffers.emit({
            ...this.selectedQ,
            request: {
                ...this.selectedQ.request,
                hosts : this.selectedQ.hotelInformations.host
            }
        });
    }

    public showList(): void {
        this.onShowList.emit();
    }

    public getQuotation(): void {
        this.onChoose.emit(
            {
                ...this.selectedQ,
                request: {
                    ...this.selectedQ.request,
                    hosts : this.selectedQ.hotelInformations.host
                }
            }
        );
    }

    // #TODO 07-05-2018_FG Ajouter à un service, même fonction que dans HotelsListComponent
    public getHotelProperty(property: IProperty): IProperty {
        if (this.selectedQuotation&& this.selectedQuotation.hotels && this.selectedQuotation.hotels.hasOwnProperty(property.property)) {
            return this.selectedQuotation.hotels[property.property].property;
        }

        return property;
    }

    public onMarkerClick(marker: IMarkerPointData): void {
        this.markerClick(marker.overlay.data);
        this.generateMarkers();
    }

    private getBounds(geoposition: IIqbhCoordinates): IMinimumBounds {
        if (!geoposition) {
            return undefined;
        }

        const east: IPoint = this.mapComputationService.computeOffset(
            {latitude: geoposition.latitude, longitude: geoposition.longitude},
            90,
            geoposition.radius.max
        );

        const west: IPoint = this.mapComputationService.computeOffset(
            {latitude: geoposition.latitude, longitude: geoposition.longitude},
            270,
            geoposition.radius.max
        );

        return {
            ne: east,
            sw: west,
        };
    }

    private generateMarkers(): void {
        this.markers = (this.overlays || []).map((overlay: IOverlay): IMarkerPointData => {
            const isSelected: boolean = this.selectedQ && this.selectedQ.request.properties === overlay.data.property;

            return {
                color: isSelected ? MarkerColor.Red : MarkerColor.Default,
                latitude: overlay.position.lat,
                longitude: overlay.position.lng,
                overlay,
                size: isSelected ? MarkerSize.Large : MarkerSize.Default,
            };
        });
    }
}

interface ISelectQuote {
    request: {
        properties: string;
        trips: string;
    };
    hotelInformations: any;
}

interface IMarkerClick {
    property: string;
    trip: string;
    hotel: any;
}

interface IIqbhCoordinates {
    latitude: number;
    longitude: number;
    radius: {
        unit: string;
        max: number;
    };
}
